import type { AxiosProgressEvent } from 'axios';
import { type AxiosRequestConfig } from 'axios';
import qs from 'qs';
import { apiClient } from './api-client';

export type TOptions = Omit<
  AxiosRequestConfig,
  'data' | 'onDownloadProgress' | 'onUploadProgress'
> & {
  withOrg?: boolean;
  body?: AxiosRequestConfig['data'];
  onDownloadProgress?: (progress: number) => void;
  onUploadProgress?: (progress: number) => void;
};

const onProgress = (
  progressEvent: AxiosProgressEvent,
  cb: (percent: number) => void
) => {
  const percent = Math.round(
    (progressEvent.loaded * 100) / (progressEvent.total || 1)
  );
  cb(percent);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const request = async <T>({
  withOrg = true,
  body,
  onDownloadProgress,
  onUploadProgress,
  ...options
}: TOptions) => {
  const isFormData = body instanceof FormData;

  const config: AxiosRequestConfig & { withOrg?: boolean } = {
    ...options,
    method: options.method ?? 'GET',
    data: body,
    withOrg,
    headers: {
      ...(isFormData
        ? { 'Content-Type': 'multipart/form-data' }
        : { 'Content-Type': 'application/json' }),
      ...options.headers,
    },

    ...(onUploadProgress && {
      onUploadProgress: (event) => onProgress(event, onUploadProgress),
    }),

    ...(onDownloadProgress && {
      onDownloadProgress: (event) => onProgress(event, onDownloadProgress),
    }),

    paramsSerializer: {
      serialize: (params) =>
        qs.stringify(params, {
          allowEmptyArrays: true,
          skipNulls: true,
          arrayFormat: 'repeat',
        }),
    },

    responseType: options.responseType ?? 'json',
  };

  const response = await apiClient(config);

  return response.data;
};
